import { createCssCompNode } from '../cssCompNode'
import type {
	Component,
	ComponentOverride,
	Docking,
	StripColumnsContainerDefinition,
	WRichTextProperties,
} from '@wix/thunderbolt-becky-types'
import type { CompNode } from '@wix/thunderbolt-catharsis'
import { createCompNode, envRefs } from '@wix/thunderbolt-catharsis'
import _ from 'lodash'
import { DEFAULT_DESKTOP_SITE_WIDTH, MOBILE_SITE_WIDTH, SOSP_CONTAINER_ID } from '../../utils/constants'
import type { SlideShowContainerDefinition } from '@wix/thunderbolt-components-native'
import { mergeCSSModes, withModes } from '../modesCompNode'
import { getVisibility, px } from '../../utils/selectorsUtils'
import type { CompsLayout, IntermediateCompMeasures, RenderableCompMeasures } from './compsMeasuresCss.types'
import { isContainer } from '../../utils/is'
import type { FeatureRefs } from '../cssFeatures.types'

const excludedComponents = new Set<string>(
	process.env.PACKAGE_NAME === 'thunderbolt-ds' ? ['PayPalButton', 'QuickActionBar'] : ['AppController']
)

const OVERRIDEN_PROPS = ['layout', 'isHiddenByModes'] as Array<keyof ComponentOverride>
const AUTO_HEIGHT = { height: 'auto' } as const
const AUTO_WIDTH = { width: 'auto' } as const

const FULL_WIDTH_MOBILE_SITE_MEASURES: CompsLayout = {
	left: '0',
	'margin-left': '0',
	width: px(MOBILE_SITE_WIDTH),
}

// SHOULD BE DELETED AFTER MAPPER IS MERGED - https://github.com/wix-private/thunderbolt/pull/27141
const isComponentSlideShow = (component: Component) =>
	component.componentType === 'SlideShowContainer' ||
	component.componentType === 'StripContainerSlideShow' ||
	component.componentType === 'BoxSlideShow'

const getFullWidthAfterMarginsSubtraction = (layout: Component['layout'], siteMargin: number = 0) => {
	const leftMargin = getDockMargin(layout, 'left')
	const rightMargin = getDockMargin(layout, 'right')
	const marginsToSubtract = [leftMargin, rightMargin, px(siteMargin), px(siteMargin)].filter((v) => v && v !== '0')

	return marginsToSubtract.length > 0 ? `calc(100% - ${marginsToSubtract.join(' - ')})` : '100%'
}

const isHorizontallyDocked = (layout: Component['layout']): boolean => !!(layout?.docked?.left && layout?.docked?.right)
const isVerticallyDocked = (layout: Component['layout']): boolean => !!(layout?.docked?.top && layout?.docked?.bottom)

const shouldOmitHeight = (component: Component) => {
	const { componentType, layout } = component
	return isHorizontallyDocked(layout) && componentType === 'TextMask'
}

const StripColumnsContainer = createCompNode({
	getDependencies: (component: Component, refs: FeatureRefs<'compsMeasuresCss'>) => ({
		compProps: refs.propertyQuery<StripColumnsContainerDefinition['property']>(component.propertyQuery!),
		deviceType: envRefs.deviceType,
		absoluteComps: envRefs.meshRuntimeData_absoluteComps,
		siteWidth: envRefs.siteWidth,
		isMobileView: envRefs.isMobileView,
		experiments: envRefs.experiments,
	}),
	toViewItem: (component, deps) => {
		const { id, layout } = component

		if (!deps.compProps?.fullWidth) {
			return { width: px(layout.width) }
		}
		if (deps.isMobileView) {
			return FULL_WIDTH_MOBILE_SITE_MEASURES
		}
		const {
			compProps: { siteMargin = 0 },
			siteWidth = DEFAULT_DESKTOP_SITE_WIDTH,
		} = deps

		const isAbsoluteStrip = deps.absoluteComps.includes(id)
		const isStripWiderThanSiteWidth = siteWidth < (layout.width || 0)
		const leftValueForAbsStrip = isStripWiderThanSiteWidth
			? px(siteMargin)
			: `calc((100% - ${px(siteWidth)}) * 0.5)`

		return _.pickBy(
			{
				left: isAbsoluteStrip ? leftValueForAbsStrip : siteMargin ? null : px(0),
				'margin-left': siteMargin ? 'auto' : getDockMargin(layout, 'left') || '0',
				'margin-right': siteMargin ? 'auto' : null,
				width: getFullWidthAfterMarginsSubtraction(layout, siteMargin),
				'min-width': isHorizontallyDocked(layout) ? 'initial' : px(siteWidth),
			},
			_.identity
		) as CompsLayout
	},
})

const fullWidth = createCompNode({
	getDependencies: (component: Component, refs: FeatureRefs<'compsMeasuresCss'>) => ({
		compProps: isComponentSlideShow(component)
			? refs.propertyQuery<SlideShowContainerDefinition['property']>(component.propertyQuery!)
			: undefined,
		siteWidth: envRefs.siteWidth,
		isMobileView: envRefs.isMobileView,
	}),
	toViewItem: (component, deps) => {
		const { isMobileView, siteWidth, compProps } = deps
		if (isComponentSlideShow(component) && compProps?.type !== 'StripContainerSlideShowProperties') {
			return defaultMeasures.toViewItem(component, deps)
		}

		const { layout, componentType } = component
		const compHeight = isContainer(componentType) ? undefined : layout.height

		if (isMobileView) {
			return { ...FULL_WIDTH_MOBILE_SITE_MEASURES, ...{ height: compHeight ? px(compHeight) : undefined } }
		}
		return _.pickBy(
			{
				left: px(0),
				'margin-left': getDockMargin(layout, 'left') || px(0),
				width: getFullWidthAfterMarginsSubtraction(layout),
				'min-width': isHorizontallyDocked(layout) ? 'initial' : px(siteWidth),
				height: compHeight ? px(compHeight) : undefined,
			},
			_.identity
		) as CompsLayout
	},
})

const getDockMargin = (layout: Component['layout'], dockKey: keyof Docking): string | null => {
	const dock = layout.docked?.[dockKey]
	const pxMargin = dock?.px
	if (pxMargin) {
		return px(pxMargin)
	}
	const vwMargin = dock?.vw
	if (vwMargin) {
		return `${vwMargin}vw`
	}
	return null
}

const defaultMeasures = createCompNode({
	getDependencies: () => ({
		isMobileView: envRefs.isMobileView,
	}),

	toViewItem: (component, deps) => getSimpleComponentMeasures(component, deps.isMobileView),
})

const WRichText = createCompNode({
	getDependencies: (component: Component, refs: FeatureRefs<'compsMeasuresCss'>) => ({
		isMobileView: envRefs.isMobileView,
		compProps: refs.propertyQuery<WRichTextProperties>(component.propertyQuery!),
	}),
	toViewItem: (component, { isMobileView, compProps }) => {
		const isVerticalText = compProps?.verticalText
		return {
			...getSimpleComponentMeasures(component, isMobileView),
			...(isVerticalText ? AUTO_WIDTH : AUTO_HEIGHT),
		}
	},
})

const getSimpleComponentMeasures = (component: Component, isMobileView: boolean): CompsLayout => {
	const { layout, componentType } = component
	const { width, height } = layout
	const widthObject = isHorizontallyDocked(layout)
		? isMobileView
			? FULL_WIDTH_MOBILE_SITE_MEASURES
			: {
				width: getFullWidthAfterMarginsSubtraction(layout),
				left: px(0),
				'margin-left': getDockMargin(layout, 'left') || px(0),
				'min-width': 'initial',
			}
		: { width: px(width) }

	return isContainer(componentType) || isVerticallyDocked(layout)
		? widthObject
		: { ...widthObject, height: px(height) }
}

const compTypeToCompNodeMapper: Record<
	string,
	CompNode<any, CompsLayout, RenderableCompMeasures & IntermediateCompMeasures>
> = {
	StripColumnsContainer,
	WRichText,
	SlideShowContainer: fullWidth,
	MasterPage: fullWidth,
	HeaderContainer: fullWidth,
	FooterContainer: fullWidth,
	PagesContainer: fullWidth,
	PageGroup: fullWidth,
	Page: fullWidth,
	ClassicSection: fullWidth,
	BgImageStrip: fullWidth,

	default: defaultMeasures,
}

const shouldNotCalculate = (component: Component, hasResponsiveLayout: boolean) =>
	hasResponsiveLayout || excludedComponents.has(component.componentType)

const compsMeasuresStyleCss = (editor: boolean) =>
	createCompNode({
		getDependencies: (component: Component, refs: FeatureRefs<'compsMeasuresCss'>) => {
			const internalCompNode =
				compTypeToCompNodeMapper[component.componentType] || compTypeToCompNodeMapper.default
			return {
				internalCompNodeDeps: internalCompNode.getDependencies(component, refs),
				hasResponsiveLayout: refs.hasResponsiveLayout(component),
			}
		},
		toViewItem: (component, { internalCompNodeDeps, hasResponsiveLayout }) => {
			if (shouldNotCalculate(component, hasResponsiveLayout)) {
				return undefined
			}

			const internalCompNode =
				compTypeToCompNodeMapper[component.componentType] || compTypeToCompNodeMapper.default

			const { layout } = component
			const compMeasures = internalCompNode.toViewItem(component, internalCompNodeDeps)
			const compHeight = compMeasures.height

			return _.pickBy(
				{
					...compMeasures,
					...(layout.rotationInDegrees && {
						transform: `rotate(${layout.rotationInDegrees}deg)`,
						'--comp-rotate-z': `${layout.rotationInDegrees}deg`,
					}),
					...(component.id !== SOSP_CONTAINER_ID && getVisibility(component)),
					// when a CollapsibleText is wrapped by autoHeightWrapper it should be height auto
					height: shouldOmitHeight(component)
						? null
						: editor
							? component.componentType === 'CollapsibleText'
								? 'auto'
								: compHeight && component.componentType === 'HoverBox'
									? `${compHeight} !important`
									: compHeight
							: compHeight,
				},
				_.identity
			)
		},
	})
export const compsMeasures = (editor: boolean) =>
	createCssCompNode(
		'compsMeasuresCss',
		'compsMeasures',
		withModes(compsMeasuresStyleCss(editor), OVERRIDEN_PROPS, mergeCSSModes)
	)
